import { Button, Dialog, DialogContent, DialogTitle, MenuItem, Select, SelectChangeEvent } from "@mui/material";
import useQrScanner, { ReadCompleted } from "hooks/useQrScanner";
import { CSSProperties, LegacyRef, useCallback, useEffect, useState } from "react";
import { useZxing } from "react-zxing";

function QRScannerVideo({
  readCompleted,
  useDeviceId,
  deviceId,
  facingMode,
  style,
}: {
  readCompleted: ReadCompleted;
  useDeviceId: boolean;
  deviceId?: string;
  facingMode: "user" | "environment";
  style: CSSProperties;
}) {
  const { ref } = useZxing({
    constraints: {
      video: useDeviceId && deviceId ? { deviceId: { exact: deviceId } } : { facingMode },
      audio: false,
    },
    onDecodeResult: (result) => readCompleted(result.getText()),
  });

  return <video ref={ref as LegacyRef<HTMLVideoElement>} style={style} muted playsInline />;
}

export default function QRScanner({
  readCompleted,
  open,
  handleClose,
}: {
  readCompleted: ReadCompleted;
  open: boolean;
  handleClose: () => void;
}) {
  const { readerProps, devices, selectedDevice, setSelectedDevice, setCameraFacing, cameraFacing } = useQrScanner({
    readCompleted,
  });
  const [useDeviceId, setUseDeviceId] = useState(false);
  const [dialogReady, setDialogReady] = useState(false);
  const deviceId = selectedDevice?.deviceId;
  const facingMode = cameraFacing ? "user" : "environment";

  useEffect(() => {
    if (open) {
      setUseDeviceId(false);
    } else {
      setDialogReady(false);
    }
  }, [open]);

  const handleDeviceChange = useCallback(
    (e: SelectChangeEvent<string>) => {
      setUseDeviceId(true);
      setSelectedDevice(devices.find((device: MediaDeviceInfo) => e.target.value === device.deviceId));
    },
    [devices, setSelectedDevice],
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      slotProps={{
        transition: {
          onEntered: () => setDialogReady(true),
          onExited: () => setDialogReady(false),
        },
      }}
    >
      <DialogTitle>Scan QR Code</DialogTitle>
      <Select size="small" fullWidth value={selectedDevice?.deviceId || ""} onChange={handleDeviceChange}>
        {devices.map((device, index) => (
          <MenuItem key={device.deviceId} value={device.deviceId}>
            {device.label.length === 0 ? `Camera # ${index + 1}` : device.label}
          </MenuItem>
        ))}
      </Select>
      <Button
        onClick={() => {
          setUseDeviceId(false);
          setCameraFacing((currentValue) => !currentValue);
        }}
      >
        {cameraFacing ? "Back Camera" : "Front Camera"}
      </Button>
      <DialogContent>
        {dialogReady && (
          <QRScannerVideo
            key={`${useDeviceId ? deviceId : facingMode}`}
            readCompleted={readCompleted}
            useDeviceId={useDeviceId}
            deviceId={deviceId}
            facingMode={facingMode}
            style={readerProps.style}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
